import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../hooks/AuthenticationProvider";
import { Plus, CheckSquare, Square, Trash2, FileText, X } from "lucide-react";
import { format, startOfWeek, startOfMonth, endOfMonth } from "date-fns";

const ScheduleEvent = ({ meeting, request, onOpenForm }) => {
  // For check-in rendering
  // For check-in rendering
  if (meeting?.isCheckIn) {
    const checkInTime = meeting.timestamp?.toDate
      ? meeting.timestamp.toDate()
      : new Date(meeting.timestamp);

    // Handle check-out time if present
    const hasCheckOut =
      meeting.checkOutTime !== null && meeting.checkOutTime !== undefined;
    const checkOutTime =
      hasCheckOut &&
      (meeting.checkOutTime?.toDate
        ? meeting.checkOutTime.toDate()
        : new Date(meeting.checkOutTime));

    return (
      <div className="group relative bg-slate-900/50 hover:bg-slate-900/80 border border-slate-700/50 rounded-lg p-3 sm:p-4 transition-all">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 sm:gap-3">
            <div
              className={`w-2 h-2 mt-2 rounded-full ${
                meeting.type === "meeting"
                  ? "bg-green-400"
                  : meeting.type === "overnight"
                  ? "bg-purple-400"
                  : "bg-blue-400"
              }`}
            />
            <div>
              <h3 className="text-white text-sm sm:text-base font-medium group-hover:text-blue-400 transition-colors">
                {meeting.meetingName ||
                  (meeting.type === "meeting"
                    ? "Check-in"
                    : meeting.type === "overnight"
                    ? "Overnight"
                    : "Other")}
              </h3>
              <div className="mt-1 text-xs sm:text-sm text-slate-400">
                <p>
                  {checkInTime.toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                  {hasCheckOut && (
                    <>
                      {" "}
                      -{" "}
                      {checkOutTime.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </>
                  )}
                </p>
                {meeting.location?.address && (
                  <p className="mt-0.5 break-words">
                    {meeting.location.address}
                  </p>
                )}
                {hasCheckOut && meeting.duration && (
                  <p className="mt-0.5 text-green-300">
                    Duration: {Math.round(meeting.duration)} min
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Status Badge - conditional styling based on type and check-out status */}
          <span
            className={`px-2 py-1 text-xs rounded-md ${
              meeting.type === "meeting"
                ? hasCheckOut
                  ? "bg-green-500/20 text-green-300"
                  : "bg-yellow-500/20 text-yellow-300"
                : meeting.type === "overnight"
                ? "bg-purple-500/20 text-purple-300"
                : "bg-blue-500/20 text-blue-300"
            }`}
          >
            {meeting.type === "meeting"
              ? hasCheckOut
                ? "Completed"
                : "Active"
              : meeting.type.charAt(0).toUpperCase() + meeting.type.slice(1)}
          </span>
        </div>
      </div>
    );
  }

  // For overnight request rendering
  if (request) {
    const formatDate = (dateStr) => {
      if (!dateStr) return "Invalid Date";
      // Make sure we're using the exact date string without timezone adjustments
      const dateParts = dateStr.split("-");
      if (dateParts.length === 3) {
        // Create date from parts to avoid timezone issues
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // JS months are 0-indexed
        const day = parseInt(dateParts[2]);
        return new Date(year, month, day).toLocaleDateString();
      }
      return new Date(dateStr).toLocaleDateString();
    };

    const locationDisplay = request.address
      ? `${request.location} - ${request.address}`
      : request.location;

    return (
      <div className="group relative bg-slate-900/50 hover:bg-slate-900/80 border border-slate-700/50 rounded-lg p-3 sm:p-4 transition-all">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="w-2 h-2 mt-2 rounded-full bg-purple-400" />
            <div>
              <h3 className="text-white text-sm sm:text-base font-medium group-hover:text-blue-400 transition-colors">
                Overnight Stay
              </h3>
              <div className="mt-1 text-xs sm:text-sm text-slate-400">
                <p>
                  {formatDate(request.startDate)}
                  {" - "}
                  <span className="text-purple-200">
                    (Returns {formatDate(request.endDate)})
                  </span>
                </p>
                <p className="mt-0.5 break-words">{locationDisplay}</p>
                {request.house && (
                  <p className="mt-0.5">House: {request.house}</p>
                )}
                {request.reviewNotes &&
                  (request.status === "approved" ||
                    request.status === "denied") && (
                    <p className="mt-2 pt-2 border-t border-slate-700/50">
                      Staff Note: {request.reviewNotes}
                    </p>
                  )}
              </div>
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs rounded-md ${
              request.status === "pending"
                ? "bg-yellow-500/20 text-yellow-300"
                : request.status === "approved"
                ? "bg-green-500/20 text-green-300"
                : "bg-red-500/20 text-red-300"
            }`}
          >
            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
          </span>
        </div>
      </div>
    );
  }

  // For meeting rendering
  return (
    <div className="group relative bg-slate-900/50 hover:bg-slate-900/80 border border-slate-700/50 rounded-lg p-3 sm:p-4 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 sm:gap-3">
          <div
            className={`w-2 h-2 mt-2 rounded-full ${
              meeting.type === "House"
                ? "bg-blue-400"
                : meeting.type === "AA"
                ? "bg-yellow-400"
                : meeting.type === "NA"
                ? "bg-green-400"
                : meeting.type === "Mandatory"
                ? "bg-red-400"
                : "bg-purple-400"
            }`}
          />
          <div>
            <h3 className="text-white text-sm sm:text-base font-medium group-hover:text-blue-400 transition-colors">
              {meeting.title}
            </h3>
            <div className="mt-1 text-xs sm:text-sm text-slate-400">
              <p>
                {new Date(`2000-01-01T${meeting.startTime}`).toLocaleTimeString(
                  [],
                  {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  }
                )}{" "}
                -{" "}
                {new Date(`2000-01-01T${meeting.endTime}`).toLocaleTimeString(
                  [],
                  {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  }
                )}
              </p>
              {meeting.location?.name && (
                <p className="mt-0.5">{meeting.location.name}</p>
              )}
            </div>
          </div>
        </div>
        <span
          className={`px-2 py-1 text-xs rounded-md ${
            meeting.type === "House"
              ? "bg-blue-500/20 text-blue-300"
              : meeting.type === "AA"
              ? "bg-yellow-500/20 text-yellow-300"
              : meeting.type === "NA"
              ? "bg-green-500/20 text-green-300"
              : meeting.type === "Mandatory"
              ? "bg-red-500/20 text-red-300"
              : "bg-purple-500/20 text-purple-300"
          }`}
        >
          {meeting.type}
        </span>
      </div>
      {meeting.location?.address && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 text-xs sm:text-sm text-slate-400">
          <p>{meeting.location.address}</p>
        </div>
      )}
      {/* Form button - made more touch-friendly */}
      {meeting.hasAttachedForm && (
        <div className="mt-2 pt-2 border-t border-slate-700/50">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onOpenForm === "function") {
                try {
                  onOpenForm(meeting);
                } catch (error) {
                  console.error("Error calling onOpenForm:", error);
                }
              } else {
                console.error("onOpenForm is not a function:", onOpenForm);
              }
            }}
            className="w-full px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <CheckSquare size={16} />
            Fill Out Form: {meeting.attachedFormName}
          </button>
        </div>
      )}
    </div>
  );
};

const FormSelector = ({ availableForms, onSelect, onCancel }) => {
  return (
    <div className="absolute z-10 mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-3">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-white text-sm font-medium">Select a Form</h4>
        <button
          onClick={onCancel}
          className="text-slate-400 hover:text-white p-1"
        >
          <X size={16} />
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {availableForms.length > 0 ? (
          <div className="space-y-2">
            {availableForms.map((form) => (
              <button
                key={form.id}
                onClick={() => onSelect(form)}
                className="w-full text-left px-3 py-2 hover:bg-slate-700 rounded-lg text-slate-300 text-sm transition-colors"
              >
                {form.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-sm py-2">No forms available</p>
        )}
      </div>
    </div>
  );
};

const ResidentOverview = ({
  meetings = [],
  overnightRequests = [],
  ActionButton,
  dailyCheckIns = [],
  residentData,
  onOpenForm,
  isStaffViewing = false,
  isScheduleLoading = false,
}) => {
  const { organizationId, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [showAddTask, setShowAddTask] = useState(false);
  const [required, setRequired] = useState(1);
  const [phaseData, setPhaseData] = useState(null);
  const [phaseRequirements, setPhaseRequirements] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [showFormSelector, setShowFormSelector] = useState(false);
  const [formTaskTitle, setFormTaskTitle] = useState("Complete Form");
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  const [selectedFormForTask, setSelectedFormForTask] = useState(null);

  const fetchAvailableForms = async () => {
    if (!organizationId) return;

    try {
      const formsRef = collection(db, `organizations/${organizationId}/forms`);
      const formsSnapshot = await getDocs(formsRef);
      const forms = formsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAvailableForms(forms);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  // Call this function when component mounts
  useEffect(() => {
    if (isStaffViewing) {
      fetchAvailableForms();
    }
  }, [isStaffViewing, organizationId]);

  useEffect(() => {
    if (!organizationId || !residentData?.id) return;

    // Only run this once when tasks are loaded, not on every task change
    const checkFormSubmissions = async () => {
      // For each task with an attached form
      const tasksWithForms = tasks.filter(
        (task) => task.hasAttachedForm && task.attachedFormId && !task.completed
      );

      if (tasksWithForms.length === 0) return;

      let updatedTasks = [...tasks];
      let needsUpdate = false;

      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      // Look up form submissions for all forms at once (single query)
      const submissionsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/formSubmissions`
      );

      const q = query(
        submissionsRef,
        where("dateSubmitted", ">=", today),
        where("dateSubmitted", "<=", endOfDay)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) return; // No submissions today

      // Create a map of formIds that have been submitted
      const submittedFormIds = new Set();
      snapshot.docs.forEach((doc) => {
        submittedFormIds.add(doc.data().formId);
      });

      // Now check which tasks need to be updated
      tasksWithForms.forEach((task) => {
        if (submittedFormIds.has(task.attachedFormId)) {
          updatedTasks = updatedTasks.map((t) => {
            if (t.id === task.id) {
              return {
                ...t,
                checked: true,
                completed: true,
                progress: t.required,
                lastCheckedDay: format(new Date(), "yyyy-MM-dd"),
              };
            }
            return t;
          });
          needsUpdate = true;
        }
      });

      // Save the updated tasks if needed
      if (needsUpdate) {
        setTasks(updatedTasks);
        await saveChecklist(updatedTasks);
      }
    };

    // Run the check only once when tasks are loaded
    checkFormSubmissions();
  }, [organizationId, residentData?.id]);

  // Get a reliable identifier for the resident
  const getResidentIdentifier = () => {
    // Log the residentData object first for debugging
    console.log("ResidentData received:", residentData);

    // If we have residentData with an ID, use that first
    if (residentData?.id) {
      console.log("Using resident ID for checklist:", residentData.id);
      return residentData.id;
    }

    // Otherwise, use the fullName from residentData if available
    if (residentData?.fullName) {
      const safeFullName = residentData.fullName
        .replace(/\s+/g, "_")
        .toLowerCase();
      console.log("Using resident fullName for checklist:", safeFullName);
      return safeFullName;
    }

    // Fall back to user email if nothing else is available
    if (user?.email) {
      console.log("Falling back to user email for checklist:", user.email);
      return user.email;
    }

    // Last resort use a timestamp-based ID if nothing else is available
    const fallbackId = `resident_${Date.now()}`;
    console.log("No identifier found, using fallback ID:", fallbackId);
    return fallbackId;
  };

  const generateOccurrences = (recurringEvent, start, end) => {
    const occurrences = [];
    const getDateValue = (dateField) => {
      if (!dateField) return null;
      const date = dateField.toDate?.() || new Date(dateField);
      return isNaN(date.getTime()) ? null : date;
    };

    const eventStart = getDateValue(recurringEvent.startDate);
    if (!eventStart) return [];

    const targetDayOfWeek = recurringEvent.dayOfWeek;
    const endDate = new Date(
      Math.min(
        getDateValue(recurringEvent.endDate)?.getTime() || end.getTime(),
        end.getTime()
      )
    );

    let currentDate = new Date(Math.max(eventStart.getTime(), start.getTime()));
    while (currentDate <= endDate) {
      if (currentDate.getDay() === targetDayOfWeek) {
        occurrences.push({
          ...recurringEvent,
          date: new Date(currentDate),
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return occurrences;
  };

  const getWeekString = () => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return format(weekStart, "yyyy-MM-dd");
  };

  const getDayString = () => format(new Date(), "yyyy-MM-dd");

  const getChecklistRef = () => {
    const residentId = getResidentIdentifier();
    return doc(db, `organizations/${organizationId}/checklists/${residentId}`);
  };

  const saveChecklist = async (updatedTasks) => {
    if (!organizationId) {
      console.log("Cannot save checklist - missing organizationId");
      return;
    }

    try {
      console.log("Saving checklist with tasks:", updatedTasks);
      const checklistRef = getChecklistRef();

      // Store the current phase info to detect phase changes
      const currentPhaseInfo = phaseData
        ? {
            phaseId: phaseData.id,
            phaseName: phaseData.name,
            phaseOrder: phaseData.order || null, // Convert undefined to null
          }
        : null;

      console.log("Current phase info:", currentPhaseInfo);

      // Ensure we're capturing all important resident information
      const residentInfo = {
        residentId: residentData?.id || "",
        residentName: residentData?.fullName || "",
        residentEmail: residentData?.email || "",
        residentHouse: residentData?.house || "",
      };

      console.log("Saving resident info:", residentInfo);

      const tasksToSave = updatedTasks.map((task) => {
        // Create a base object with all required fields
        const taskObject = {
          id: task.id,
          title: task.title || "",
          checked: task.checked || false,
          progress: task.progress || 0,
          lastCheckedDay: task.lastCheckedDay || null, // Convert undefined to null
          required: task.required || 1,
          phaseRequirement: task.phaseRequirement || false,
          requirementId: task.requirementId || null, // Convert undefined to null
          frequency: task.frequency || "weekly",
          permanent: task.permanent || false,
        };

        // Only add monthlyStartDate if it exists and frequency is monthly
        if (task.frequency === "monthly") {
          taskObject.monthlyStartDate =
            task.monthlyStartDate || new Date().toISOString();
        }

        return taskObject;
      });

      // Create document object removing any undefined values
      const docData = {
        tasks: tasksToSave,
        weekStart: getWeekString(),
        lastUpdated: new Date(),
        lastCheckedDay: getDayString(),
        currentPhase: currentPhaseInfo,
        ...residentInfo,
      };

      // Remove any potential undefined values
      Object.keys(docData).forEach((key) => {
        if (docData[key] === undefined) {
          delete docData[key];
        }
      });

      await setDoc(checklistRef, docData);

      console.log(
        "Checklist saved successfully with resident info:",
        residentInfo
      );
    } catch (error) {
      console.error("Error saving checklist:", error);
      // Add more detailed error logging
      console.error("Error details:", {
        organizationId,
        residentId: getResidentIdentifier(),
        tasks: updatedTasks.length,
      });
    }
  };

  const loadSavedChecklist = async (initialTasks) => {
    if (!organizationId) {
      console.log("Missing organizationId, can't load checklist");
      return initialTasks;
    }

    try {
      const checklistRef = getChecklistRef();
      const checklistDoc = await getDoc(checklistRef);
      const today = format(new Date(), "yyyy-MM-dd");
      const currentWeek = getWeekString();

      console.log("Initial tasks before loading saved state:", initialTasks);

      if (checklistDoc.exists()) {
        const savedData = checklistDoc.data();
        console.log("Found saved checklist data:", savedData);

        const isNewWeek = savedData.weekStart !== currentWeek;

        if (isNewWeek) {
          console.log(
            "New week detected, processing task resets appropriately"
          );

          // When it's a new week, we should reset weekly tasks but handle monthly and one-time tasks differently
          const tasksWithResetProgress = initialTasks.map((task) => {
            // Find corresponding saved task if it exists
            const savedTask = savedData.tasks.find((t) => t.id === task.id);

            if (!savedTask) {
              console.log(
                `No saved data for task ${task.id}, using initial state`
              );
              return task; // No saved data, use initial state
            }

            // Handle monthly requirements
            if (task.phaseRequirement && task.frequency === "monthly") {
              console.log(`Processing monthly task: ${task.title}`);

              // If we have a monthlyStartDate, check if 30 days have passed
              if (savedTask.monthlyStartDate) {
                const monthlyStartDate = new Date(savedTask.monthlyStartDate);
                const currentDate = new Date();

                // Calculate days elapsed since the monthly period started
                const daysDiff = Math.floor(
                  (currentDate - monthlyStartDate) / (1000 * 60 * 60 * 24)
                );
                console.log(`Monthly task started ${daysDiff} days ago`);

                // If 30 days have passed, reset the monthly task
                if (daysDiff >= 30) {
                  console.log(
                    `Monthly period of 30 days completed, resetting task: ${task.title}`
                  );
                  return {
                    ...task,
                    progress: 0,
                    completed: false,
                    checked: false,
                    lastCheckedDay: null,
                    monthlyStartDate: currentDate.toISOString(), // Reset the monthly period
                  };
                }

                // Still within monthly period, preserve progress
                console.log(
                  `Monthly task still within 30-day period, preserving progress: ${savedTask.progress}/${task.required}`
                );
                return {
                  ...task,
                  progress: savedTask.progress,
                  completed: savedTask.progress >= task.required,
                  checked: false,
                  lastCheckedDay: null,
                  monthlyStartDate: savedTask.monthlyStartDate,
                };
              } else {
                // No monthlyStartDate found, initialize one
                console.log(
                  `Initializing monthly task with start date: ${new Date().toISOString()}`
                );
                return {
                  ...task,
                  progress: savedTask.progress || 0,
                  completed: (savedTask.progress || 0) >= task.required,
                  checked: false,
                  lastCheckedDay: null,
                  monthlyStartDate: new Date().toISOString(),
                };
              }
            }

            // For one-time requirements (frequency='total'), we keep the progress across weeks
            if (task.phaseRequirement && task.frequency === "total") {
              console.log(
                `Preserving progress for one-time task: ${task.title}`
              );
              return {
                ...task,
                progress: savedTask.progress,
                completed: savedTask.progress >= task.required,
                checked: false,
                lastCheckedDay: null,
              };
            }

            // For all other tasks (weekly and daily), reset progress for the new week
            console.log(
              `Resetting weekly/daily task for new week: ${task.title}`
            );
            return {
              ...task,
              progress: 0,
              checked: false,
              completed: false,
              lastCheckedDay: null,
            };
          });

          // Save the updated tasks for the new week
          await saveChecklist(tasksWithResetProgress);
          return tasksWithResetProgress;
        }

        // If it's the same week, merge saved state with current tasks
        // Process existing tasks and carry over their progress
        const updatedTasks = initialTasks.map((task) => {
          const savedTask = savedData.tasks.find((t) => t.id === task.id);
          if (savedTask) {
            console.log(`Found saved data for task ${task.id}:`, savedTask);
            // Important fix: determine completion based on progress vs required
            const isCompleted = (savedTask.progress ?? 0) >= task.required;
            return {
              ...task,
              // Only mark as checked if checked today AND not completed
              // This ensures completed tasks that were unchecked stay unchecked
              checked: savedTask.lastCheckedDay === today && savedTask.checked,
              progress: savedTask.progress ?? 0,
              lastCheckedDay: savedTask.lastCheckedDay ?? null,
              completed: isCompleted,
              // Preserve the monthly start date for monthly tasks
              monthlyStartDate:
                task.frequency === "monthly"
                  ? savedTask.monthlyStartDate || new Date().toISOString()
                  : undefined,
            };
          }
          return task;
        });

        // Get custom tasks (user-added tasks that aren't in initialTasks)
        const customTasks = savedData.tasks.filter(
          (savedTask) =>
            !initialTasks.some((task) => task.id === savedTask.id) &&
            !savedTask.permanent &&
            !savedTask.phaseRequirement
        );

        const combinedTasks = [...updatedTasks, ...customTasks];
        return combinedTasks;
      }

      console.log("No saved checklist found, creating new one");
      await saveChecklist(initialTasks);
      return initialTasks;
    } catch (error) {
      console.error("Error loading saved checklist:", error);
      return initialTasks;
    }
  };

  // Fetch resident's current phase data
  const fetchPhaseData = async () => {
    // Add more defensive coding to handle various edge cases
    if (!residentData) {
      console.log("No residentData available");
      return null;
    }

    if (!organizationId) {
      console.log("No organizationId available");
      return null;
    }

    if (!residentData.phase) {
      console.log("Resident has no phase assigned:", residentData);
      return null;
    }

    try {
      console.log("Fetching phase data for phase:", residentData.phase);

      // Query directly for the specific phase by name first
      const phasesRef = collection(
        db,
        `organizations/${organizationId}/phases`
      );
      const phaseQuery = query(
        phasesRef,
        where("name", "==", residentData.phase)
      );
      const phaseSnapshot = await getDocs(phaseQuery);

      // If direct query found a result, use it
      if (!phaseSnapshot.empty) {
        const phaseDoc = phaseSnapshot.docs[0];
        const currentPhase = {
          id: phaseDoc.id,
          ...phaseDoc.data(),
        };

        console.log("Found phase by direct query:", currentPhase);

        if (currentPhase.requirements && currentPhase.requirements.length > 0) {
          console.log(
            `Found ${currentPhase.requirements.length} requirements in phase`
          );
          setPhaseData(currentPhase);
          setPhaseRequirements(currentPhase.requirements);
          return currentPhase;
        } else {
          console.log("Phase has no requirements:", currentPhase);
        }
      } else {
        // Fallback to getting all phases if direct query didn't work
        console.log("Phase not found by direct query, fetching all phases");
        const allPhasesSnapshot = await getDocs(phasesRef);

        const phases = allPhasesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log(
          "All phases:",
          phases.map((p) => ({ id: p.id, name: p.name }))
        );

        // Try exact match first
        let currentPhase = phases.find(
          (phase) => phase.name === residentData.phase
        );

        // If no exact match, try case-insensitive match
        if (!currentPhase) {
          console.log("No exact match, trying case-insensitive match");
          currentPhase = phases.find(
            (phase) =>
              phase.name &&
              phase.name.toLowerCase() === residentData.phase.toLowerCase()
          );
        }

        console.log("Found current phase:", currentPhase);

        if (
          currentPhase &&
          currentPhase.requirements &&
          currentPhase.requirements.length > 0
        ) {
          console.log(
            `Found ${currentPhase.requirements.length} requirements in phase`
          );
          setPhaseData(currentPhase);
          setPhaseRequirements(currentPhase.requirements);
          return currentPhase;
        } else if (currentPhase) {
          console.log("Phase has no requirements:", currentPhase);
        }
      }

      return null;
    } catch (error) {
      console.error("Error fetching phase data:", error);
      return null;
    }
  };

  // Convert phase requirements to tasks
  const convertRequirementsToTasks = (requirements) => {
    if (!requirements || !requirements.length) {
      console.log("No requirements to convert to tasks");
      return [];
    }

    console.log("Converting requirements to tasks:", requirements);

    // Make sure we're working with an array
    const requirementsArray = Array.isArray(requirements)
      ? requirements
      : [requirements];

    // Filter out invalid requirements
    const validRequirements = requirementsArray.filter((req) => {
      if (!req) {
        console.log("Found null or undefined requirement");
        return false;
      }

      if (!req.id) {
        console.log("Found requirement without ID:", req);
        return false;
      }

      if (!req.description) {
        console.log("Found requirement without description:", req);
        return false;
      }

      return true;
    });

    console.log(
      `Found ${validRequirements.length} valid requirements out of ${requirementsArray.length}`
    );

    const tasks = validRequirements.map((req) => {
      // Determine the appropriate time display
      let timeDisplay = "Weekly";
      if (req.frequency === "daily") timeDisplay = "Daily";
      else if (req.frequency === "monthly") timeDisplay = "Monthly";
      else if (req.frequency === "total") timeDisplay = "One-time";

      // Make sure count is a number
      const count =
        typeof req.count === "number"
          ? req.count
          : typeof req.count === "string"
          ? parseInt(req.count, 10)
          : 1;

      // Create a task ID that's unique but consistent for this requirement
      const taskId = `phase-req-${req.id}`;

      const task = {
        id: taskId,
        title: req.description,
        time: timeDisplay,
        required: count,
        progress: 0,
        checked: false,
        completed: false,
        permanent: true,
        phaseRequirement: true,
        requirementId: req.id,
        frequency: req.frequency || "weekly",
        hasAttachedForm: req.hasAttachedForm || false,
        attachedFormId: req.attachedFormId || null,
        attachedFormName: req.attachedFormName || null,
      };

      console.log("Created task from requirement:", task);
      return task;
    });

    console.log(`Converted ${tasks.length} phase requirements to tasks`);
    return tasks;
  };

  // Fetch tasks including phase requirements
  useEffect(() => {
    const fetchTasks = async () => {
      // Check that we have the necessary data to proceed
      if (!organizationId) {
        console.log("No organizationId available, skipping task fetch");
        return;
      }

      // Log residentData to help diagnose issues
      console.log("Starting to fetch tasks with residentData:", residentData);

      try {
        // First check if there's a saved checklist with different phase requirements
        const checklistRef = getChecklistRef();
        const checklistDoc = await getDoc(checklistRef);
        let savedPhase = null;

        if (checklistDoc.exists()) {
          const savedData = checklistDoc.data();
          savedPhase = savedData.currentPhase;
          console.log("Saved phase data:", savedPhase);
          console.log("Resident info in checklist:", {
            residentId: savedData.residentId,
            residentName: savedData.residentName,
          });
        }

        // Check if resident's phase changed
        const currentPhaseName = residentData?.phase;
        const phaseChanged =
          savedPhase &&
          currentPhaseName &&
          savedPhase.phaseName !== currentPhaseName;

        if (phaseChanged) {
          console.log(
            "Phase changed from",
            savedPhase.phaseName,
            "to",
            currentPhaseName
          );
        }

        // Initial basic tasks
        let initialTasks = [
          {
            id: "morning-gratitude",
            title: "Morning Gratitude List",
            time: "7:00 AM",
            required: 7,
            progress: 0,
            checked: false,
            completed: false,
            permanent: true,
          },
          {
            id: "sponsor-call",
            title: "Call Your Sponsor",
            time: "9:00 AM",
            required: 7,
            progress: 0,
            checked: false,
            completed: false,
            permanent: true,
          },
          {
            id: "nightly-reflection",
            title: "Nightly Reflection",
            time: "9:00 PM",
            required: 7,
            progress: 0,
            checked: false,
            completed: false,
            permanent: true,
          },
        ];

        console.log(
          "Core tasks before adding phase requirements:",
          initialTasks
        );

        // Fetch phase data and requirements
        const phase = await fetchPhaseData();
        console.log("Fetched phase data:", phase);

        // Add phase requirements to tasks if available
        if (phase && phase.requirements && phase.requirements.length > 0) {
          const phaseRequirementTasks = convertRequirementsToTasks(
            phase.requirements
          );
          console.log("Phase requirement tasks to add:", phaseRequirementTasks);

          if (phaseRequirementTasks.length > 0) {
            initialTasks = [...initialTasks, ...phaseRequirementTasks];
            console.log(
              "Combined tasks with phase requirements:",
              initialTasks
            );
          }
        } else {
          console.log("No phase requirements to add to tasks");
        }

        // If phase changed, we need to reset saved checklist to include new phase requirements
        if (phaseChanged) {
          console.log(
            "Phase changed, resetting checklist with new phase requirements"
          );
          await saveChecklist(initialTasks);
          setTasks(initialTasks);
        } else {
          // Load any saved state for these tasks (if phase hasn't changed)
          const tasksWithSavedStates = await loadSavedChecklist(initialTasks);
          console.log("Final tasks with saved states:", tasksWithSavedStates);
          setTasks(tasksWithSavedStates);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchTasks();

    // Set up daily reset timer
    const now = new Date();
    const tomorrow = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    tomorrow.setHours(0, 0, 0, 0);
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();

    const dailyReset = setTimeout(() => {
      window.location.reload();
    }, timeUntilMidnight);

    return () => clearTimeout(dailyReset);
  }, [organizationId, residentData]);

  // Update the overnightRequests filter in todayMeetings
  const todayMeetings = [
    ...meetings.filter((meeting) => {
      if (!meeting?.startDate) return false;
      const today = new Date();

      if (meeting.frequency) {
        const monthStart = startOfMonth(today);
        const monthEnd = endOfMonth(today);
        const occurrences = generateOccurrences(meeting, monthStart, monthEnd);

        return occurrences.some((occurrence) => {
          const occurrenceDate = new Date(occurrence.date);
          return (
            occurrenceDate.getDate() === today.getDate() &&
            occurrenceDate.getMonth() === today.getMonth() &&
            occurrenceDate.getFullYear() === today.getFullYear()
          );
        });
      }

      const meetingDate = new Date(meeting.startDate);
      return (
        meetingDate.getDate() === today.getDate() &&
        meetingDate.getMonth() === today.getMonth() &&
        meetingDate.getFullYear() === today.getFullYear()
      );
    }),
    ...overnightRequests
      .filter((request) => {
        console.log("Processing overnight request in todayMeetings:", request);

        if (!request?.startDate || !request?.endDate) {
          console.log("Request missing dates:", request);
          return false;
        }

        // Fix date handling by parsing date strings properly
        // Format: YYYY-MM-DD - ensure consistent timezone handling
        const today = new Date();
        const todayFormatted = today.toISOString().split("T")[0]; // Get YYYY-MM-DD

        // Get the day before end date (last night away)
        const endDate = new Date(request.endDate + "T00:00:00"); // Add time component to avoid timezone issues
        const lastNightDate = new Date(endDate);
        lastNightDate.setDate(lastNightDate.getDate() - 1);
        const lastNightFormatted = lastNightDate.toISOString().split("T")[0];

        // Simple string comparison for dates in YYYY-MM-DD format
        const isWithinDateRange =
          todayFormatted >= request.startDate &&
          todayFormatted <= lastNightFormatted;

        // Check if this overnight request belongs to the current resident
        const isCurrentResident =
          String(request.residentId) === String(residentData?.id);

        const includeRequest =
          (request.status === "pending" || request.status === "approved") &&
          isWithinDateRange &&
          isCurrentResident;

        console.log("Should include overnight in schedule?", includeRequest, {
          todayFormatted,
          startDate: request.startDate,
          endDate: request.endDate,
          lastNightFormatted,
          isWithinRange: isWithinDateRange,
          isCurrentResident,
          status: request.status,
        });

        return includeRequest;
      })
      .map((request) => {
        console.log("Adding overnight to schedule:", request.id);
        return {
          ...request,
          isOvernight: true,
          // Ensure we're not modifying the original dates
          startDate: request.startDate,
          endDate: request.endDate,
          // Give it top priority in sorting
          priority: 1,
        };
      }),
    // Add resident's check-ins for today
    ...dailyCheckIns.map((checkIn) => ({
      ...checkIn,
      isCheckIn: true,
      // Give check-ins lower priority than overnights but higher than regular meetings
      priority: 2,
    })),
  ]
    // First sort by priority (overnights first)
    .sort((a, b) => {
      // First, check for the priority field (set for overnights)
      if (a.priority && !b.priority) return -1;
      if (!a.priority && b.priority) return 1;

      // If both are overnights or neither are overnights
      if (a.isOvernight && !b.isOvernight) return -1;
      if (!a.isOvernight && b.isOvernight) return 1;

      // If both are the same type, sort by time
      const aTime = a.startTime
        ? new Date(`2000-01-01T${a.startTime}`)
        : new Date(`${a.startDate}T00:00:00`);
      const bTime = b.startTime
        ? new Date(`2000-01-01T${b.startTime}`)
        : new Date(`${b.startDate}T00:00:00`);
      return aTime - bTime;
    });

  // Log the final array for debugging
  console.log("Final todayMeetings array:", todayMeetings);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;

    const newTaskObj = {
      id: Date.now().toString(),
      title: newTask,
      time: "Custom",
      required: required,
      progress: 0,
      checked: false,
      completed: false,
      permanent: false,
      // Add form properties if a form is selected
      hasAttachedForm: !!selectedFormForTask,
      attachedFormId: selectedFormForTask?.id || null,
      attachedFormName: selectedFormForTask?.name || null,
    };

    const updatedTasks = [...tasks, newTaskObj];
    setTasks(updatedTasks);
    await saveChecklist(updatedTasks);
    setNewTask("");
    setRequired(1);
    setShowAddTask(false);
    setSelectedFormForTask(null);
  };

  const toggleTask = async (taskId) => {
    console.log(`Toggling task: ${taskId}`);
    const today = format(new Date(), "yyyy-MM-dd");

    const updatedTasks = tasks.map((task) => {
      if (task.id === taskId) {
        // Get current state
        const isCurrentlyChecked = task.checked;

        // Calculate the new state
        const willBeChecked = !isCurrentlyChecked;
        console.log(
          `Task ${task.id} will be changed from ${
            isCurrentlyChecked ? "checked" : "unchecked"
          } to ${willBeChecked ? "checked" : "unchecked"}`
        );

        // Special case: Handle tasks with 0 required (shouldn't be completable)
        if (task.required === 0) {
          console.log(
            `Task ${task.id} has required=0, setting progress=0 and completed=false`
          );
          return {
            ...task,
            checked: willBeChecked,
            progress: 0,
            completed: false,
            lastCheckedDay: today,
          };
        }

        // Calculate new progress based on check action
        let newProgress = task.progress;

        if (task.lastCheckedDay === today) {
          // If already interacted with today, increment or decrement
          newProgress = willBeChecked
            ? task.progress + 1
            : Math.max(task.progress - 1, 0);
          console.log(
            `Task already interacted with today, new progress: ${newProgress}`
          );
        } else {
          // First interaction of the day
          newProgress = willBeChecked ? task.progress + 1 : task.progress;
          console.log(
            `First interaction with task today, new progress: ${newProgress}`
          );
        }

        // Determine if the task is now completed based on progress
        const isNowCompleted = newProgress >= task.required;
        console.log(
          `Task is now ${
            isNowCompleted ? "completed" : "not completed"
          } with progress ${newProgress}/${task.required}`
        );

        return {
          ...task,
          checked: willBeChecked,
          progress: newProgress,
          completed: isNowCompleted,
          lastCheckedDay: today,
        };
      }
      return task;
    });

    console.log("Updated tasks after toggle:", updatedTasks);
    setTasks(updatedTasks);
    await saveChecklist(updatedTasks);
  };

  // Modified deleteTask function to delete any type of task
  const deleteTask = async (taskId) => {
    const taskToDelete = tasks.find((task) => task.id === taskId);

    if (!taskToDelete) {
      console.log("Task not found for deletion:", taskId);
      return;
    }

    console.log("Deleting task:", taskToDelete);

    const updatedTasks = tasks.filter((task) => task.id !== taskId);
    setTasks(updatedTasks);
    await saveChecklist(updatedTasks);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Quick Actions - Mobile optimized */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 w-full">
        {ActionButton}
      </div>

      {/* Main Content Grid - Stacked on mobile, side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Today's Schedule */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-white">
              Today's Schedule
            </h2>
            <span className="text-xs sm:text-sm text-slate-400">
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>

          {isScheduleLoading ? (
            <div className="text-center py-6 sm:py-8">
              <div className="animate-pulse text-slate-400 text-sm">
                Loading schedule...
              </div>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {todayMeetings.length > 0 ? (
                todayMeetings.map((item, idx) => (
                  <ScheduleEvent
                    key={item.id || idx}
                    meeting={!item.isOvernight ? item : undefined}
                    request={item.isOvernight ? item : undefined}
                    onOpenForm={onOpenForm}
                  />
                ))
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <p className="text-slate-400 text-sm">
                    No events scheduled for today
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Daily Checklist */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-white">
              Daily Checklist
            </h2>
            <button
              onClick={() => setShowAddTask(true)}
              className="p-2 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-colors"
            >
              <Plus className="text-blue-400" size={18} />
            </button>
          </div>

          {showAddTask && (
            <div className="mb-4 sm:mb-6 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Add a new task..."
                  className="flex-1 bg-slate-800/50 rounded-lg px-3 py-2 text-sm sm:text-base text-white border border-slate-600 focus:border-blue-500 transition-colors"
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={required}
                    onChange={(e) => setRequired(parseInt(e.target.value))}
                    className="w-16 sm:w-20 bg-slate-800/50 rounded-lg px-3 py-2 text-sm sm:text-base text-white border border-slate-600 focus:border-blue-500 transition-colors"
                  />
                  <button
                    onClick={handleAddTask}
                    className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-slate-400">
                Enter task name and number of times per week (1-7)
              </p>

              {/* Only show form attachment option if in staff view */}
              {isStaffViewing && (
                <div className="relative mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center">
                      <input
                        type="checkbox"
                        id="attachForm"
                        checked={!!selectedFormForTask}
                        onChange={() => {
                          if (selectedFormForTask) {
                            setSelectedFormForTask(null);
                          } else {
                            setShowFormSelector(true);
                          }
                        }}
                        className="mr-2"
                      />
                      <label
                        htmlFor="attachForm"
                        className="text-slate-300 text-xs sm:text-sm"
                      >
                        Attach a form to this task
                      </label>
                    </div>

                    {selectedFormForTask && (
                      <div className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                        <FileText size={12} />
                        <span className="truncate max-w-[100px] sm:max-w-[180px]">
                          {selectedFormForTask.name}
                        </span>
                        <button
                          onClick={() => setSelectedFormForTask(null)}
                          className="ml-1 text-blue-300 hover:text-blue-100"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {showFormSelector && (
                    <FormSelector
                      availableForms={availableForms} // Pass availableForms here
                      onSelect={(form) => {
                        setSelectedFormForTask(form);
                        setShowFormSelector(false);
                        // Optionally update the task title to include the form name
                        if (
                          newTask.trim() === "" ||
                          newTask === "Complete Form"
                        ) {
                          setNewTask(`Complete ${form.name}`);
                        }
                      }}
                      onCancel={() => setShowFormSelector(false)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            {/* Core Tasks */}
            <div className="mb-4">
              <h3 className="text-blue-300 text-xs sm:text-sm font-medium mb-2 uppercase tracking-wide">
                TASKS
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {tasks
                  .filter((task) => !task.phaseRequirement)
                  .sort((a, b) => {
                    // Sort completed tasks to the bottom
                    if (a.completed && !b.completed) return 1;
                    if (!a.completed && b.completed) return -1;
                    return 0;
                  })
                  .map((task) => (
                    <div
                      key={task.id}
                      className={`flex flex-col p-3 sm:p-4 rounded-lg transition-colors ${
                        task.completed || task.checked
                          ? "bg-slate-800/30"
                          : "bg-slate-800/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <button
                            onClick={() => toggleTask(task.id)}
                            className="flex-shrink-0 cursor-pointer"
                          >
                            {task.completed || task.checked ? (
                              <CheckSquare
                                className="text-blue-400"
                                size={18}
                              />
                            ) : (
                              <Square className="text-slate-400" size={18} />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p
                                className={`text-base sm:text-lg ${
                                  task.completed && task.required > 0
                                    ? "text-slate-400 line-through"
                                    : "text-white"
                                } ${
                                  expandedTaskId === task.id ? "" : "truncate"
                                } cursor-pointer`}
                                onClick={() =>
                                  setExpandedTaskId(
                                    expandedTaskId === task.id ? null : task.id
                                  )
                                }
                                title={task.title} // Optional: Tooltip fallback
                              >
                                {task.title}
                              </p>

                              <span className="text-xs sm:text-sm text-slate-400 ml-2 flex-shrink-0">
                                {task.progress}/{task.required}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-400 truncate">
                              {task.time}
                            </p>
                          </div>
                          <button
                            onClick={() => deleteTask(task.id)}
                            className="p-1.5 sm:p-2 hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
                          >
                            <Trash2 className="text-red-400" size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Add form button if the task has an attached form */}
                      {task.hasAttachedForm && task.attachedFormId && (
                        <div className="mt-2 pt-2 border-t border-slate-700/50">
                          <button
                            onClick={() => {
                              if (typeof onOpenForm === "function") {
                                onOpenForm({
                                  id: task.id,
                                  hasAttachedForm: true,
                                  attachedFormId: task.attachedFormId,
                                  attachedFormName: task.attachedFormName,
                                  title: task.title,
                                });
                              }
                            }}
                            className="w-full px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
                          >
                            <CheckSquare size={16} />
                            Fill Out Form: {task.attachedFormName || "Form"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Phase Requirements */}
            {tasks.filter((task) => task.phaseRequirement).length > 0 && (
              <div className="mb-4">
                <h3 className="text-indigo-300 text-xs sm:text-sm font-medium mb-2 uppercase tracking-wide">
                  PHASES TO DO
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  {tasks
                    .filter((task) => task.phaseRequirement)
                    .sort((a, b) => {
                      // Sort completed tasks to the bottom
                      if (a.completed && !b.completed) return 1;
                      if (!a.completed && b.completed) return -1;
                      return 0;
                    })
                    .map((task) => (
                      <div
                        key={task.id}
                        className={`flex flex-col p-3 sm:p-4 rounded-lg transition-colors ${
                          task.completed || task.checked
                            ? "bg-indigo-900/20"
                            : "bg-indigo-900/10"
                        } border border-indigo-800/30`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <button
                              onClick={() => toggleTask(task.id)}
                              className="flex-shrink-0 cursor-pointer"
                            >
                              {task.completed || task.checked ? (
                                <CheckSquare
                                  className="text-indigo-400"
                                  size={18}
                                />
                              ) : (
                                <Square className="text-slate-400" size={18} />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p
                                  className={`text-base sm:text-lg ${
                                    task.completed
                                      ? "text-slate-400 line-through"
                                      : "text-white"
                                  } ${
                                    expandedTaskId === task.id ? "" : "truncate"
                                  } cursor-pointer`}
                                  onClick={() =>
                                    setExpandedTaskId(
                                      expandedTaskId === task.id
                                        ? null
                                        : task.id
                                    )
                                  }
                                  title={task.title}
                                >
                                  {task.title}
                                </p>

                                <span className="text-xs sm:text-sm text-slate-400 ml-2 flex-shrink-0">
                                  {task.progress}/{task.required}
                                </span>
                              </div>
                              <p className="text-xs sm:text-sm text-indigo-200 truncate">
                                {task.frequency === "weekly"
                                  ? "Weekly Requirement"
                                  : task.frequency === "monthly"
                                  ? "Monthly Requirement"
                                  : task.frequency === "daily"
                                  ? "Daily Requirement"
                                  : "One-time Requirement"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Add form button if the task has an attached form */}
                        {task.hasAttachedForm && task.attachedFormId && (
                          <div className="mt-2 pt-2 border-t border-slate-700/50">
                            <button
                              onClick={() => {
                                if (typeof onOpenForm === "function") {
                                  onOpenForm({
                                    id: task.id,
                                    hasAttachedForm: true,
                                    attachedFormId: task.attachedFormId,
                                    attachedFormName: task.attachedFormName,
                                    title: task.title,
                                  });
                                }
                              }}
                              className="w-full px-3 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-lg text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
                            >
                              <CheckSquare size={16} />
                              Fill Out Form: {task.attachedFormName || "Form"}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentOverview;
